import cx from 'classnames';
import {useCallback} from 'react';
import {useSelector} from 'react-redux';
import {userIsLoggedIn} from '~/common/fabric/User/reducers/user';
import {useEventSave} from '~/OneStepCreateInvitation/hooks/useEventSave';
import {selectEventId} from '~/OneStepCreateInvitation/reducers/event/selector';
import {Input} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';
import {useIsSmallAndUp} from '~library/hooks/useMatchQuery';
import {EditRemoveButton} from '~library/molecules/EditRemoveButton';
import './OptionWrapper.sass';

export const OptionWrapper = ({
  icon,
  title,
  subtitle,
  onEdit,
  isActive,
  onRemove,
  activeClass = '',
  noChildrenDefaultStyle = false,
  hideEdit,
  showDiamond,
  children,
  qaIdPrefix = title.replace(/[^A-Z]/gi, '-').toLowerCase(),
}) => {
  const {autoSave} = useEventSave();
  const eventId = useSelector(selectEventId);
  const loggedIn = useSelector(userIsLoggedIn);
  const isSmallAndUp = useIsSmallAndUp();
  const dataQaId = `${qaIdPrefix}-gifting-option`;

  const toggle = useCallback(() => {
    if (isActive) {
      return onRemove();
    }

    return onEdit();
  }, [isActive, onRemove, onEdit]);

  const onToggleClickHandler = useCallback(async () => {
    if (!loggedIn) {
      const futureSave = autoSave();
      if (await futureSave) {
        toggle();
      }
    } else if (!eventId) {
      const futureSave = autoSave();
      toggle();
      await futureSave;
    } else {
      toggle();
    }
  }, [loggedIn, autoSave, eventId, toggle]);

  return (
    <div className={cx('gift-option__wrapper', {'gift-option__wrapper--active': isActive})}>
      <div className="gift-option">
        <div className="gift-option__icon">{icon}</div>
        <div className="gift-option__titles">
          <Typography
            variant={isSmallAndUp ? 'header6' : 'list2'}
            as="h3"
            size="large"
            className="gift-option__titles__title"
          >
            {title}
          </Typography>
          <Typography variant="paragraph3" color="pebble">
            {subtitle}
          </Typography>
        </div>

        <div className="gift-option__action">
          <Input
            type="toggle"
            checked={isActive}
            onChange={onToggleClickHandler}
            data-qa-id={dataQaId}
            aria-label={title}
          />
        </div>
      </div>
      {isActive && children && (
        <div
          className={cx({
            'gift-option__active': !noChildrenDefaultStyle,
            [activeClass]: !!activeClass,
          })}
        >
          <div className="gift-option__action-row">
            <EditRemoveButton
              editLabel="Edit"
              editButtonAriaLabel={`${title} Edit`}
              onEditClick={onEdit}
              hideEdit={hideEdit}
              hideRemove
              dataQaIdSuffix={dataQaId}
              showDiamond={showDiamond}
            />
          </div>
          {children}
        </div>
      )}
    </div>
  );
};
